import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

// Generate or retrieve unique device ID for this browser
const getOrCreateDeviceId = () => {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    // Create unique device ID for this browser instance
    deviceId = `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in (token exists)
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      // User is authenticated - don't call login again
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      // Get or create unique device ID for this browser
      const deviceId = getOrCreateDeviceId();
      
      // Add device_id to credentials
      const response = await authAPI.login({
        ...credentials,
        device_id: deviceId,
        deviceToken: 'web-token'
      });
      
      if (response.data.success) {
        const userData = response.data.data;
        const token = response.data.token;
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Store token for API authentication
        if (token) {
          localStorage.setItem('token', token);
          // Store token creation time for debugging
          localStorage.setItem('token_created_at', Date.now().toString());
        }
        
        return { success: true, data: userData, token };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      
      if (response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message , errors: response.data.errors};
      }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Registration failed',
        errors: error.response?.data?.errors
      };
    }
  };

  const updateUser = (updatedData) => {
    setUser(updatedData);
    localStorage.setItem('user', JSON.stringify(updatedData));
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('token_created_at');
      // Don't remove device_id - keep it for future logins from same browser
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

